import clsx from "clsx";

type Props = {
  label: string;
  checked: boolean;
  onChange: () => void;
};

/**
 * Filter toggle sized for the filter row.
 *
 * The shared ToggleSwitch is a fixed-size CSS control built for form rows; dropped
 * into a 40px pill it reads as an oversized blob next to the date chip. This is the
 * same control at chip scale, and it also carries its state in fill and label — not
 * just in knob position — so "on" is legible at a glance.
 */
export default function ToggleChip({ label, checked, onChange }: Props) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={clsx(
        "h-10 pl-2.5 pr-4 rounded-full flex items-center gap-2.5 border cursor-pointer",
        "text-[13px] font-medium transition-all duration-300 outline-none",
        checked
          ? "bg-accent/10 border-accent/30 text-accent"
          : "bg-surface border-edge text-text-secondary hover:border-edge-strong hover:text-text-primary"
      )}
    >
      <span
        aria-hidden
        className={clsx(
          "relative w-8 h-[18px] rounded-full shrink-0 transition-colors duration-300",
          checked ? "bg-accent" : "bg-edge-strong"
        )}
      >
        <span
          className={clsx(
            "absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow-sm transition-all duration-300",
            checked ? "left-[16px]" : "left-[2px]"
          )}
        />
      </span>
      <span className="text-nowrap">{label}</span>
    </button>
  );
}
